import OmniTable from "../../omni-common/components/OmniTable";
import OmniCheckbox from "../../omni-common/components/OmniCheckbox";
import { formatDate } from "../../../utilities/format";
import { ChevronUpDownIcon, ChevronUpIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import { OriginalPostStatus } from "../../../lib/apollo/graphql/omni/omni-generated";

export interface OriginalPost {
  id: string;
  title: string;
  status: OriginalPostStatus | "NEW" | "DRAFTED" | "DRAFT_REQUESTED" | "DRAFT_REQUEST_FAILED" | "ON_HOLD" | "SELECTED";
  tags: string[];
  createdAt: string;
  updatedAt: string;
  content?: string;
  memo?: string;
  sourceUrl?: string;
  scheduledAt?: string;
  authorName?: string;
  draft?: {
    draftId: string;
  };
}

interface OriginalPostTableProps {
  posts: OriginalPost[];
  selectedIds: string[];
  onSelectAll: (selected: boolean) => void;
  onSelectPost: (id: string, selected: boolean) => void;
  onPostClick?: (post: OriginalPost) => void;
  onPublishSingle?: (postId: string) => void;
  isLoading?: boolean;
  sortKey?: string;
  sortDirection?: "ASC" | "DESC";
  onSort?: (key: string, direction: "ASC" | "DESC") => void;
}

const STATUS_LABELS: Record<string, string> = {
  NEW: "신규",
  DRAFTED: "발행완료",
  DRAFT_REQUESTED: "발행중",
  DRAFT_REQUEST_FAILED: "발행실패",
  ON_HOLD: "보류",
  SELECTED: "발행예정",
  DELETED: "삭제됨",
  REJECTED: "거부됨",
};


const STATUS_COLORS: Record<string, string> = {
  NEW: "bg-blue-100 text-blue-800 border border-blue-200",
  DRAFTED: "bg-emerald-100 text-emerald-800 border border-emerald-200",
  DRAFT_REQUESTED: "bg-green-100 text-green-800 border border-green-200",
  DRAFT_REQUEST_FAILED: "bg-red-100 text-red-800 border border-red-200",
  ON_HOLD: "bg-gray-100 text-gray-800 border border-gray-200",
  SELECTED: "bg-yellow-100 text-yellow-800 border border-yellow-200",
  DELETED: "bg-red-100 text-red-800 border border-red-200",
  REJECTED: "bg-orange-100 text-orange-800 border border-orange-200",
};

export default function OriginalPostTable({
  posts,
  selectedIds,
  onSelectAll,
  onSelectPost,
  onPostClick,
  onPublishSingle,
  isLoading,
  sortKey,
  sortDirection,
  onSort,
}: OriginalPostTableProps) {
  const isAllSelected = posts.length > 0 && selectedIds.length === posts.length;
  

  const handleSort = (key: string) => {
    if (!onSort) return;

    // 첫 클릭: ASC, 두 번째 클릭: DESC, 세 번째 클릭: 정렬 초기화
    if (sortKey === key) {
      if (sortDirection === "ASC") {
        onSort(key, "DESC");
      } else {
        // 정렬 초기화 (빈 문자열로 전달)
        onSort("", "ASC");
      }
    } else {
      onSort(key, "ASC");
    }
  };

  const SortableHeader = ({ 
    sortKey: key, 
    children, 
    className,
    style 
  }: { 
    sortKey?: string; 
    children: React.ReactNode; 
    className?: string;
    style?: React.CSSProperties;
  }) => {
    const isSortable = key && onSort;
    const isActive = sortKey === key;
    
    if (!isSortable) {
      return <div className={className} style={style}>{children}</div>;
    }

    const getSortIcon = () => {
      if (isActive) {
        return sortDirection === "ASC" ? (
          <ChevronUpIcon className="h-4 w-4 ml-1" />
        ) : (
          <ChevronDownIcon className="h-4 w-4 ml-1" />
        );
      }
      return <ChevronUpDownIcon className="h-4 w-4 ml-1" />;
    };

    return (
      <div 
        className={`${className} cursor-pointer transition-colors hover:bg-gray-800 ${
          isActive ? 'bg-gray-800' : ''
        }`}
        style={style}
        onClick={() => handleSort(key)}
      >
        {children}
        {getSortIcon()}
      </div>
    );
  };

  const TagColumn = ({ 
    tags, 
    maxDisplayCount,
    style 
  }: { 
    tags: string[]; 
    maxDisplayCount: number;
    style?: React.CSSProperties;
  }) => {
    if (tags.length === 0) {
      return (
        <div 
          className="flex items-center justify-center border-e px-2"
          style={style}
        >
          <span className="text-xs text-gray-400">-</span>
        </div>
      );
    }

    const displayTags = tags.slice(0, maxDisplayCount);
    const remainingCount = tags.length - maxDisplayCount;

    return (
      <div 
        className="flex items-center justify-center border-e px-2"
        style={style}
      >
        <div className="flex flex-wrap gap-1 justify-center items-center max-w-full">
          {displayTags.map((tag, index) => (
            <span
              key={index}
              className="rounded bg-gray-100 px-2 py-1 text-xs text-gray-600 whitespace-nowrap max-w-[80px] truncate inline-block"
              title={tag}
            >
              {tag}
            </span>
          ))}
          {remainingCount > 0 && (
            <span 
              className="text-xs text-gray-500 font-medium bg-gray-50 rounded px-2 py-1"
              title={`${remainingCount}개의 추가 태그: ${tags.slice(maxDisplayCount).join(', ')}`}
            >
              +{remainingCount}
            </span>
          )}
        </div>
      </div>
    );
  };


  return (
    <div className="relative h-full flex flex-col">
      <OmniTable.Wrapper className="flex-1">
      <div className="flex w-full items-center rounded-t-lg border border-black bg-black py-2">
        <div className="flex items-center justify-center px-2 text-sm font-bold text-white" style={{ width: '63px', minWidth: '63px' }}>
          <OmniCheckbox
            isChecked={isAllSelected}
            onCheck={onSelectAll}
          />
        </div>
        <div
          className="flex items-center justify-center px-2 text-sm font-bold text-white"
          style={{ width: '90px', minWidth: '90px' }}
        >
          상태
        </div>
        <SortableHeader
          sortKey="title"
          className="flex items-center justify-center px-2 text-sm font-bold text-white flex-1"
          style={{ minWidth: '200px' }}
        >
          제목
        </SortableHeader>
        <div
          className="flex items-center justify-center px-2 text-sm font-bold text-white"
          style={{ width: '245px', minWidth: '245px' }}
        >
          태그
        </div>
        <SortableHeader
          sortKey="createdAt"
          className="flex items-center justify-center px-2 text-sm font-bold text-white"
          style={{ width: '105px', minWidth: '105px' }}
        >
          등록일자
        </SortableHeader>
        <SortableHeader
          sortKey="updatedAt"
          className="flex items-center justify-center px-2 text-sm font-bold text-white"
          style={{ width: '105px', minWidth: '105px' }}
        >
          수정일자
        </SortableHeader>
        <div
          className="flex items-center justify-center px-2 text-sm font-bold text-white"
          style={{ width: '80px', minWidth: '80px' }}
        >
          발행
        </div>
      </div>
      
      <OmniTable.Body isLoading={isLoading} isEmpty={!posts?.length}>
        {posts?.map((post) => {
          const isSelected = selectedIds.includes(post.id);
          
          return (
            <div
              key={post.id}
              className="relative"
              data-post-id={post.id}
            >
              <OmniTable.Row
                onClick={() => onPostClick?.(post)}
              >
              <div 
                className="flex items-center justify-center border-e px-2"
                style={{ width: '63px', minWidth: '63px' }}
              >
                <div onClick={(e) => e.stopPropagation()}>
                  <OmniCheckbox
                    isChecked={isSelected}
                    onCheck={(checked: boolean) => onSelectPost(post.id, checked)}
                    disabled={post.status === "DRAFT_REQUESTED" || post.status === "DRAFTED"}
                  />
                </div>
              </div>
              
              <div 
                className="flex items-center justify-center border-e px-2"
                style={{ width: '90px', minWidth: '90px' }}
              >
                <span
                  className={`rounded-full px-3 py-1 text-xs font-semibold ${
                    STATUS_COLORS[post.status]
                  }`}
                >
                  {STATUS_LABELS[post.status]}
                </span>
              </div>
              
              <div 
                className="flex items-center justify-start border-e px-2 flex-1"
                style={{ minWidth: '200px' }}
              >
                <span className="truncate" title={post.title}>
                  {post.title}
                </span>
              </div>
              
              <TagColumn
                tags={post.tags}
                maxDisplayCount={3}
                style={{ width: '245px', minWidth: '245px' }}
              />

              <div
                className="flex items-center justify-center border-e px-2 text-sm"
                style={{ width: '105px', minWidth: '105px' }}
              >
                {formatDate(post.createdAt, "YY.MM.DD")}
              </div>
              
              <div 
                className="flex items-center justify-center border-e px-2 text-sm"
                style={{ width: '105px', minWidth: '105px' }}
              >
                {formatDate(post.updatedAt, "YY.MM.DD")}
              </div>
              
              <div
                className="flex items-center justify-center px-2"
                style={{ width: '80px', minWidth: '80px' }}
              >
                <button
                  className={`rounded px-2 py-1 text-xs text-white transition-colors ${
                    post.status === "SELECTED"
                      ? "bg-blue-500 hover:bg-blue-600"
                      : "bg-gray-400 cursor-not-allowed"
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (post.status === "SELECTED") {
                      onPublishSingle?.(post.id);
                    }
                  }}
                  disabled={post.status !== "SELECTED"}
                >
                  발행
                </button>
              </div>
            </OmniTable.Row>
          </div>
          );
        })}
      </OmniTable.Body>
      </OmniTable.Wrapper>
    </div>
  );
}